import React, { useState } from 'react';
import { Sparkles, PencilLine, Plus } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';

interface SkillOption {
  id: string;
  name: string;
  display_name: string;
  description: string;
  kind: string;
  source: string;
}

interface StepProps {
  result: {
    ui_step: string;
    signal_name: string;
    skills: SkillOption[];
  };
  onSelect: (value: string, label: string) => void;
}

const SkillSelectorStep: React.FC<StepProps> = ({ result, onSelect }) => {
  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (skill: SkillOption) => {
    setSelected(skill.id);
    onSelect(skill.id, skill.display_name || skill.name);
  };

  const handleSkip = () => {
    setSelected('skip');
    onSelect('skip', 'No analysis needed');
  };

  const handleCreateNew = () => {
    setSelected('create_new');
    onSelect('create_new', 'Create a new skill for this signal');
  };

  const skills = result.skills || [];

  return (
    <div className="w-full max-w-lg">
      <p className="mb-1 text-sm font-medium text-gray-700">
        Choose an analysis for this signal
      </p>
      <p className="mb-3 text-xs text-gray-500">
        Pick an analysis to generate for{' '}
        <span className="font-medium text-gray-700">"{result.signal_name}"</span>,
        or create a new one
      </p>

      <div className="space-y-2">
        {skills.map((skill) => (
          <button
            key={skill.id}
            type="button"
            onClick={() => handleSelect(skill)}
            disabled={selected !== null}
            className={cn(
              'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all',
              selected === skill.id
                ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500'
                : selected !== null
                  ? 'border-gray-200 bg-white opacity-50 cursor-not-allowed'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 cursor-pointer',
            )}
          >
            <div
              className={cn(
                'mt-0.5 rounded-md p-1.5',
                selected === skill.id ? 'bg-violet-100 text-violet-600' : 'bg-gray-100 text-gray-500',
              )}
            >
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-700">
                {skill.display_name || skill.name}
              </p>
              {skill.description && (
                <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{skill.description}</p>
              )}
            </div>
          </button>
        ))}

        {selected === null && (
          <>
            <button
              type="button"
              onClick={handleCreateNew}
              className="flex w-full items-center gap-3 rounded-lg border border-dashed border-gray-300 p-3 text-left text-sm text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700"
            >
              <div className="rounded-md bg-gray-100 p-1.5 text-gray-500">
                <Plus className="h-4 w-4" />
              </div>
              Create a new analysis for this signal
            </button>
            <button
              type="button"
              onClick={handleSkip}
              className="flex w-full items-center gap-2 rounded-lg p-2 text-xs text-gray-400 transition-colors hover:text-gray-600"
            >
              <PencilLine className="h-3.5 w-3.5" />
              Skip — no analysis needed
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SkillSelectorStep;
